// Tax Return Types

export type ExtensionFilingMethod = 'e-filed' | 'paper' | 'previously-filed';

export interface ExtensionInfo {
  extensionFiledAt: string;             // ISO date
  extensionFilingMethod: ExtensionFilingMethod;
  extensionConfirmation?: string | null;
}

// A single Schedule C business linked to an individual return. `id` is the Dataverse account id
// when linked (used to resolve the Xero contact for billing); null for manually-entered businesses.
export interface ScheduleCBusiness {
  id?: string | null;
  name?: string | null;
  ein?: string | null;
  phone?: string | null;
}

// Canonical read path for a return's Schedule C businesses: prefer the list, else synthesize a
// one-element list from the legacy scalar fields (un-backfilled returns). Mirrors the backend helper.
export function getScheduleCBusinesses(tr: Pick<TaxReturn,
  'scheduleCBusinesses' | 'scheduleCBusinessName' | 'scheduleCBusinessEIN' | 'scheduleCBusinessPhone' | 'scheduleCBusinessId'>
): ScheduleCBusiness[] {
  if (tr.scheduleCBusinesses && tr.scheduleCBusinesses.length > 0) return tr.scheduleCBusinesses;
  if (tr.scheduleCBusinessName || tr.scheduleCBusinessId) {
    return [{
      id: tr.scheduleCBusinessId ?? null,
      name: tr.scheduleCBusinessName ?? null,
      ein: tr.scheduleCBusinessEIN ?? null,
      phone: tr.scheduleCBusinessPhone ?? null,
    }];
  }
  return [];
}

export interface TaxReturn {
  id: string;
  appointmentId: string;
  linkedAppointmentIds: string[];
  taxYear: number;
  returnType: 'individual' | 'business';
  isAmendment?: boolean;
  /** Wave 2B: explicit primary-return flag for a multi-return client+year tie-break. Nothing sets
   * this true yet (api/Models/TaxReturn.cs, measured 2026-11-01). */
  isPrimary?: boolean;
  /** Whether the client is an in-house (bookkeeping/payroll/sales-tax) client. Populated only by
   * GetPreparerByYearRoster (api/Functions/PreparerByYearFunctions.cs) -- undefined/null on every
   * other read path, including every other TaxReturn this type describes. */
  isInHouse?: boolean | null;
  /** The client's most-recently-seen portal device language (ClientDevice.Language), NOT a stored
   * preference -- there is no persisted "client language" field in this backend. Null when the
   * client has no device record, or none carries a language. Populated only by
   * GetPreparerByYearRoster, same as isInHouse above. */
  clientLanguage?: string | null;
  filingType: FilingType;

  // Client Information
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  contactId?: string | null;
  contactName?: string | null;

  // Spouse Information (for joint filers)
  spouseId?: string | null;
  spouseName?: string | null;
  spouseEmail?: string | null;
  spousePhone?: string | null;

  // Schedule C Businesses (single-member LLCs / Schedule C entities linked to an individual return).
  // Canonical list; the scalar fields below mirror the primary (first) business for back-compat.
  scheduleCBusinesses?: ScheduleCBusiness[];
  scheduleCBusinessName?: string | null;
  scheduleCBusinessEIN?: string | null;
  scheduleCBusinessPhone?: string | null;
  scheduleCBusinessId?: string | null;

  billingName?: string | null;

  // Linked Returns (for related returns in same appointment)
  linkedReturnIds: string[];
  parentReturnId?: string;

  // Business owner link
  businessOwnerId?: string;
  businessOwnerName?: string;
  businessEntityType?: BusinessEntityType;

  // Source tracking
  createdFrom: 'appointment' | 'manual' | 'linked';

  // Appointment Details
  appointmentType: 'in-person' | 'virtual' | 'drop-off';
  scheduledDate: string;
  services: string;

  // Status
  status: TaxReturnStatus;
  statusHistory: StatusChange[];

  // Document Tracking
  allDocumentsReceived: boolean;
  missingDocuments: string[];
  documentsReceivedDate?: string;

  // Review Tracking
  changesNeededAfterReview: boolean;
  reviewNotes?: string;
  reviewCompletedDate?: string;

  // E-File Tracking
  efileStatus: 'pending' | 'submitted' | 'accepted' | 'rejected';
  efileSubmittedDate?: string;
  efileAcceptedDate?: string;
  efileRejectionReason?: string;

  // Extension Tracking
  extensionFiledAt?: string | null;
  extensionFiledBy?: string | null;
  extensionFilingMethod?: ExtensionFilingMethod | null;
  extensionConfirmation?: string | null;

  // Signature Tracking
  signatureStatus: 'pending' | 'sent' | 'signed';
  signatureRequestId?: string;
  signatureSentDate?: string;
  signatureCompletedDate?: string;
  signatureFormType?: Form8879Type;
  linkedSignatures?: LinkedSignature[];

  // Authorized Signer (for business returns)
  authorizedSignerName?: string;
  authorizedSignerEmail?: string;

  // Billing
  clientInvoice: InvoiceInfo;
  additionalInvoices?: InvoiceInfo[];
  totalInvoicedAmount?: number;
  preparerBillAmount?: number;
  invoiceId?: string;
  preparerBillId?: string;
  sharedInvoiceId?: string;
  invoiceAllocationAmount?: number;
  invoiceAllocationPaid?: boolean;

  // Cash Payment (no invoice)
  cashPaymentAmount?: number;
  cashPaymentDate?: string;
  cashPaymentReference?: string;
  cashPaymentRecordedBy?: string;

  // Staff Assignment
  preparerId: string;
  preparerName: string;
  preparerEmail?: string;
  reviewerId?: string;
  reviewerName?: string;
  reviewerEmail?: string;

  // EIN (last 4 digits) for unlocking password-protected Drake PDFs
  lastFourEin?: string | null;

  // AI-Extracted Data
  extractedData?: TaxReturnExtractedData;

  // AI-Extracted Invoice Data (may have suggested amount)
  extractedInvoiceData?: ExtractedInvoiceData;

  // Reminder Tracking
  documentReminderCount?: number;
  documentReminderLastSentAt?: string;
  efileRejectionReminderCount?: number;
  efileRejectionReminderLastSentAt?: string;

  // Expected Documents (from Drake checklist)
  expectedDocuments?: string[];

  // Notes/Comments
  notes: TaxReturnNote[];

  // Conversation
  conversationId?: string;

  // Department
  department: string;

  // Metadata
  createdAt: string;
  updatedAt: string;
  completedAt?: string;

  // Appointment Sync Tracking
  lastSyncedAt?: string;
  appointmentCancelledAt?: string;
  cancellationReason?: string;
  isCancelled?: boolean;

  // Searchable aliases (merged business names, old names, spouse names)
  searchableAliases: string[];

  // Review status
  reviewStatus: ReviewStatus;
}

export type TaxReturnStatus =
  | 'scheduled'
  | 'pending_resolution'
  | 'documents_pending'
  | 'ready_to_prepare'
  | 'in_preparation'
  | 'in_review'
  | 'needs_corrections'
  | 'ready_for_signature'
  | 'waiting_for_payment'
  | 'ready_for_efile'
  | 'e_filed'
  | 'completed'
  | 'inactive';

export type InactiveReason =
  | 'client_no_show'
  | 'not_filing_this_year'
  | 'went_to_another_preparer'
  | 'duplicate_entry'
  | 'other';

export const inactiveReasonOptions: { value: InactiveReason; label: string }[] = [
  { value: 'client_no_show', label: 'Client No-Show' },
  { value: 'not_filing_this_year', label: 'Not Filing This Year' },
  { value: 'went_to_another_preparer', label: 'Went to Another Preparer' },
  { value: 'duplicate_entry', label: 'Duplicate Entry' },
  { value: 'other', label: 'Other' },
];

export type FilingType = 'single' | 'joint' | 'mfs' | 'hoh' | 'business';

export type ReviewStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'completed_pending_payment'
  | 'changes_required_preparer'
  | 'changes_required_taxpayer';

export interface StatusChange {
  fromStatus: TaxReturnStatus | null;
  toStatus: TaxReturnStatus;
  changedAt: string;
  changedBy?: string;
  changedByName?: string;
  notes?: string;
}

export interface InvoiceInfo {
  id?: string;
  status: 'not_generated' | 'generated' | 'paid';
  amount?: number;
  description?: string;
  generatedDate?: string;
  paidDate?: string;
  invoiceNumber?: string;
  paymentUrl?: string;     // Xero online invoice URL for client payment
  xeroUrl?: string;        // Link to invoice in Xero dashboard
}

export interface ExtractedInvoiceData {
  totalAmount?: number;
  lineItems?: Array<{
    description: string;
    amount: number;
    quantity: number;
  }>;
}

export interface TaxReturnExtractedData {
  refundOrPayment: 'refund' | 'payment' | 'zero';
  amount: number;
  agi?: number;
  totalTax?: number;
  totalIncome?: number;
  filingStatus?: string;
  extractedAt: string;
  sourceFileName?: string;
}

export interface TaxReturnNote {
  id: string;
  content: string;
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  isImportant: boolean;
}

// Tax Document Types

export interface TaxDocument {
  id: string;
  taxReturnId: string;
  fileName: string;
  fileType: string;
  contentType: string;
  fileSize: number;
  blobUrl: string;
  blobName: string;
  documentType: string;
  documentYear?: number;
  issuerName?: string;
  uploadSource: 'portal' | 'email' | 'staff' | 'zoho_sign';
  uploadedBy?: string;
  uploadedByName?: string;
  uploadedAt: string;
  sourceReturnId?: string;
  emailFrom?: string;
  emailSubject?: string;
  emailReceivedAt?: string;
  signatureRequestId?: string;
  signedAt?: string;
}

// Upload Link Types

export interface DocumentUploadLink {
  id: string;
  clientId: string;
  taxReturnId: string;
  token: string;
  expiresAt: string;
  maxUploads: number;
  currentUploads: number;
  clientName: string;
  clientEmail?: string;
  requiredDocuments: string[];
  customMessage?: string;
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  lastAccessedAt?: string;
  isActive: boolean;
}

// System User Types

export interface SystemUser {
  systemUserId: string;
  fullName: string;
  email?: string;
  jobTitle?: string;
  isDisabled: boolean;
  roles?: string[];
}

// Staff Roles

export type StaffRoleType = 'admin' | 'preparer' | 'reviewer' | 'reception' | 'staff';

export interface StaffRole {
  id: string;
  systemUserId: string;
  fullName: string;
  email?: string;
  roles: StaffRoleType[];
  /** For preparers: systemUserId of their assigned reviewer. */
  reviewerId?: string;
  isActive: boolean;
  updatedAt: string;
  updatedBy?: string;
}

// Dashboard Types

export interface DashboardStats {
  totalReturns: number;
  returnsByStatus: Record<string, number>;
  pendingDocuments: number;
  pendingSignatures: number;
  pendingPayments: number;
  efileRejections: number;
  completedThisWeek: number;
  unassignedDropoffs: number;
  returnsByPreparer: Record<string, number>;
}

// A single return surfaced in one of the dashboard "outstanding items" queues.
export interface OutstandingReturn {
  id: string;
  clientName: string;
  clientEmail?: string;
  preparerName?: string;
  status: string;
  details: string;
}

export interface OutstandingItems {
  missingDocuments: OutstandingReturn[];
  pendingSignatures: OutstandingReturn[];
  unpaidInvoices: OutstandingReturn[];
  efileRejections: OutstandingReturn[];
  // Returns e-filed and awaiting the taxing authority's response (not yet accepted or rejected).
  pendingEFileAcceptance: OutstandingReturn[];
}

// API Response Types

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Filter Types

export type ScheduledRange = 'today' | 'this-week' | 'next-week' | 'this-month' | 'past-due';

export interface TaxReturnFilters {
  status?: TaxReturnStatus;
  preparerId?: string;
  taxYear?: number;
  appointmentType?: string;
  scheduledRange?: ScheduledRange;
  clientId?: string;
  appointmentId?: string;
  unassigned?: boolean;
}

export interface BulkStatusUpdateResult {
  updated: number;
  failed: number;
  skipped: number;
  errors: string[];
}

// Status display helpers

export const statusDisplayNames: Record<TaxReturnStatus, string> = {
  scheduled: 'Scheduled',
  pending_resolution: 'Pending Resolution',
  documents_pending: 'Documents Pending',
  ready_to_prepare: 'Ready to Prepare',
  in_preparation: 'In Preparation',
  in_review: 'In Review',
  needs_corrections: 'Needs Corrections',
  ready_for_signature: 'Ready for Signature',
  waiting_for_payment: 'Waiting for Payment',
  ready_for_efile: 'Ready for E-File',
  e_filed: 'E-Filed',
  completed: 'Completed',
  inactive: 'Inactive',
};

export const statusColors: Record<TaxReturnStatus, string> = {
  scheduled: '#6B7280',         // gray
  pending_resolution: '#EF4444', // red - needs attention
  documents_pending: '#F59E0B', // amber
  ready_to_prepare: '#22C55E',  // green - ready for preparer pickup
  in_preparation: '#3B82F6',    // blue
  in_review: '#8B5CF6',         // purple
  needs_corrections: '#F97316', // orange
  ready_for_signature: '#EC4899', // pink
  waiting_for_payment: '#D97706', // amber-darker — distinct from documents_pending amber
  ready_for_efile: '#14B8A6',   // teal
  e_filed: '#10B981',           // green
  completed: '#059669',         // emerald
  inactive: '#9CA3AF',          // gray
};

export const scheduledRangeDisplayNames: Record<ScheduledRange, string> = {
  'today': 'Today',
  'this-week': 'This Week',
  'next-week': 'Next Week',
  'this-month': 'This Month',
  'past-due': 'Past Due',
};

export const documentTypeDisplayNames: Record<string, string> = {
  w2: 'W-2',
  '1099_misc': '1099-MISC',
  '1099_nec': '1099-NEC',
  '1099_int': '1099-INT',
  '1099_div': '1099-DIV',
  '1099_b': '1099-B',
  '1099_r': '1099-R',
  k1: 'K-1',
  '1098': '1098 Mortgage Interest',
  '1098_t': '1098-T Tuition',
  property_tax: 'Property Tax Statement',
  charitable_donations: 'Charitable Donations',
  medical_expenses: 'Medical Expenses',
  business_expenses: 'Business Expenses',
  prior_year_return: 'Prior Year Return',
  id_verification: 'ID Verification',
  unsigned_8879: 'Unsigned 8879',
  signed_8879: 'Signed 8879',
  engagement_letter: 'Engagement Letter',
  completed_return: 'Completed Tax Return',
  other: 'Other Document',
};

// Document types that make up the "Signature Documents" section of the Documents tab.
// Shared so the section grouping and the Send-for-Signature dropdown can't drift apart.
export const signatureDocumentTypes: string[] = ['unsigned_8879', 'signed_8879', 'engagement_letter'];

// ============ APPOINTMENT ATTENDEES ============

export interface AppointmentAttendee {
  activityPartyId: string;
  partyId: string;
  partyName: string;
  partyType: 'contact' | 'account' | 'systemuser';
  participationType: number;
  email?: string;
  phone?: string;
  primaryContactId?: string;
  primaryContactName?: string;
}

export interface AppointmentAttendeesResponse {
  appointmentId: string;
  appointmentSubject?: string;
  scheduledDate: string;
  attendees: AppointmentAttendee[];
  existingRelationship?: ClientRelationship;
  needsResolution: boolean;
  existingReturns: TaxReturnSummary[];
}

export interface TaxReturnSummary {
  id: string;
  clientName: string;
  status: TaxReturnStatus;
  filingType: FilingType;
  returnType: 'individual' | 'business';
}

// ============ ATTENDEE RESOLUTION ============

export interface ResolveAttendeesRequest {
  resolutionType: 'joint' | 'separate';
  taxpayer?: AttendeeSelection;
  spouse?: AttendeeSelection;
  savePreference: boolean;
  taxYear: number;
}

export interface AttendeeSelection {
  contactId: string;
  contactName: string;
  email?: string;
  phone?: string;
}

export interface AddReturnToAppointmentRequest {
  returnType: 'individual' | 'business';
  contactId?: string;
  accountId?: string;
  parentReturnId?: string;
  taxYear: number;
  isAmendment?: boolean;
}

// ============ CLIENT RELATIONSHIPS ============

export interface ClientRelationship {
  id: string;
  primaryContactId: string;
  primaryContactName: string;
  primaryContactEmail?: string;
  relatedContacts: RelatedContact[];
  relationshipType: 'joint' | 'separate';
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  createdByName?: string;
  department: string;
}

export interface RelatedContact {
  contactId: string;
  contactName: string;
  contactEmail?: string;
  contactPhone?: string;
  relationshipRole: 'spouse' | 'dependent' | 'business_owner' | 'separate_filer';
}

// ============ DATAVERSE SEARCH ============

export interface ContactInfo {
  contactId: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
  parentAccountId?: string;
  parentAccountName?: string;
}

export interface AccountInfo {
  accountId: string;
  name: string;
  email?: string;
  phone?: string;
  primaryContactId?: string;
  primaryContactName?: string;
}

export interface SearchResults {
  contacts: ContactInfo[];
  accounts: AccountInfo[];
}

// ============ FILING TYPE HELPERS ============

// ============ APPOINTMENT TYPE HELPERS ============

export type AppointmentType = 'in-person' | 'virtual' | 'drop-off';

export const appointmentTypeDisplayNames: Record<AppointmentType, string> = {
  'in-person': 'In-Person',
  'virtual': 'Virtual',
  'drop-off': 'Drop-Off',
};

export const appointmentTypeColors: Record<AppointmentType, string> = {
  'in-person': '#0078d4',   // blue
  'virtual': '#8B5CF6',     // purple
  'drop-off': '#F97316',    // orange
};

export const appointmentTypeIcons: Record<AppointmentType, string> = {
  'in-person': 'PersonRegular',
  'virtual': 'VideoRegular',
  'drop-off': 'BoxRegular',
};

/**
 * Returns true if the appointment type requires Zoho Sign for signatures
 * (virtual and drop-off need remote signatures; in-person signs on-site)
 */
export function requiresRemoteSignature(appointmentType: AppointmentType | string): boolean {
  return appointmentType === 'virtual' || appointmentType === 'drop-off';
}

/**
 * Returns true if the appointment type is virtual (client expected to submit docs beforehand)
 */
export function isVirtualAppointment(appointmentType: AppointmentType | string): boolean {
  return appointmentType === 'virtual';
}

export const filingTypeDisplayNames: Record<FilingType, string> = {
  single: 'Single',
  joint: 'Married Filing Jointly',
  mfs: 'Married Filing Separately',
  hoh: 'Head of Household',
  business: 'Business',
};

export const filingTypeColors: Record<FilingType, string> = {
  single: '#3B82F6',    // blue
  joint: '#8B5CF6',     // purple
  mfs: '#EC4899',       // pink
  hoh: '#F59E0B',       // amber
  business: '#10B981',  // green
};

// 3-way amendment filter for the returns list. Spans all return types, so labels are generic.
export type AmendmentFilter = 'all' | 'amendments' | 'exclude';

export const amendmentFilterOptions: { value: AmendmentFilter; label: string }[] = [
  { value: 'all', label: 'All Returns' },
  { value: 'amendments', label: 'Amendments only' },
  { value: 'exclude', label: 'Exclude amendments' },
];

// Amendment labels are type-aware: individuals file 1040X; businesses amend on their base form
// (S-corp 1120-S / partnership 1065 with the "amended" box, C-corp via 1120-X). The amended-form
// token is empty for a business whose entity sub-type isn't known yet (e.g. at creation time).
export function amendmentFormToken(
  returnType?: string,
  businessEntityType?: BusinessEntityType,
): string {
  if (returnType === 'business') {
    switch (businessEntityType) {
      case 's-corp': return '1120-S';
      case 'c-corp': return '1120-X';
      case 'partnership': return '1065';
      default: return '';
    }
  }
  return '1040X';
}

// Full label for amendment toggles, e.g. "Amended return (1040X)" / "Amended return (1120-S)" /
// "Amended return" (business, sub-type unknown).
export function amendmentToggleLabel(
  returnType?: string,
  businessEntityType?: BusinessEntityType,
): string {
  const token = amendmentFormToken(returnType, businessEntityType);
  return token ? `Amended return (${token})` : 'Amended return';
}

// Short badge text for the returns list: keep the familiar "1040X" for individuals, "Amended" for
// businesses (the specific form rides in the tooltip).
export function amendmentBadgeLabel(returnType?: string): string {
  return returnType === 'business' ? 'Amended' : '1040X';
}

// ============ BOOKKEEPING CLIENT REVIEW ============

export interface BookkeepingClientReview {
  id: string;
  clientId: string;
  clientName: string;
  reviewPeriod?: string;
  reviewYear?: number;
  reviewMonth?: number;
  status?: string;
  reviewCompleted: boolean;
  reviewCompletedDate?: string;
  assignedTo?: string;
  assignedToName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ClientBookkeepingStatus {
  isBookkeepingClient: boolean;
  latestReview?: BookkeepingClientReview;
  reviewsCompleted: number;
  reviewsPending: number;
  allReviews: BookkeepingClientReview[];
}

// ============ DRAKE PDF EXTRACTION ============

export interface DrakeChecklist {
  id: string;
  clientId?: string;
  taxYear: number;
  sourceTaxYear: number;
  extractedClientInfo: ExtractedClientInfo;
  matchStatus: DrakeMatchStatus;
  matchCandidates: string[];
  matchedAt?: string;
  matchedBy?: string;
  checklistItems: DrakeChecklistItem[];
  questionnaireItems: DrakeQuestionnaireItem[];
  priorYearAmounts?: PriorYearAmounts;
  extractionMetadata: ExtractionMetadata;
  corrections: DrakeCorrection[];
  usedInOutreach: OutreachUsage[];
  linkedTaxReturnId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExtractedClientInfo {
  name?: string;
  spouseName?: string;
  ssnLast4?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface DrakeChecklistItem {
  category: string;
  documentType: string;
  payerName?: string;
  ein?: string;
  entityType?: string;
  isRequired: boolean;
  notes?: string;
  priorYearAmount?: number;
  received: boolean;
  receivedAt?: string;
}

export interface DrakeQuestionnaireItem {
  category: string;
  question: string;
  priorYearAnswer?: string;
  isRelevant: boolean;
}

export interface PriorYearAmounts {
  totalIncome?: number;
  agi?: number;
  totalTax?: number;
  refundOrOwed?: number;
  federalWithholding?: number;
  stateWithholding?: number;
}

export interface ExtractionMetadata {
  pdfBlobName?: string;
  originalFileName?: string;
  extractedAt: string;
  extractionModel: string;
  confidence: number;
  processingTimeMs: number;
  pageCount: number;
  extractedPages: number[];
  rawResponse?: string;
}

export interface DrakeCorrection {
  field: string;
  originalValue?: string;
  correctedValue?: string;
  correctedAt: string;
  correctedBy?: string;
  correctedByName?: string;
}

export interface OutreachUsage {
  outreachId: string;
  sentAt: string;
}

export type DrakeMatchStatus = 'pending_review' | 'matched' | 'multiple_matches' | 'no_match';

export const drakeMatchStatusDisplayNames: Record<DrakeMatchStatus, string> = {
  pending_review: 'Pending Review',
  matched: 'Matched',
  multiple_matches: 'Multiple Matches',
  no_match: 'No Match',
};

export const drakeMatchStatusColors: Record<DrakeMatchStatus, string> = {
  pending_review: '#F59E0B', // amber
  matched: '#10B981',       // green
  multiple_matches: '#8B5CF6', // purple
  no_match: '#EF4444',      // red
};

// ============ DRAKE PASSWORD-PROTECTED PDF ============

export interface PasswordProtectedPdfError {
  errorCode: 'PDF_PASSWORD_REQUIRED';
  message: string;
  blobName: string;
  originalFileName: string;
  taxYear: number;
}

export interface RetryWithPasswordRequest {
  blobName: string;
  password: string;
  taxYear: number;
  uploadedBy?: string;
  uploadedByName?: string;
}

// ============ DRAKE PHASE 4: ANALYTICS & LEARNING ============

export interface DrakeExtractionAnalytics {
  totalExtractions: number;
  totalCorrections: number;
  correctionRate: number;
  autoMatchRate: number;
  averageConfidence: number;
  averageProcessingTimeMs: number;
  correctionsByField: FieldCorrectionStats[];
  correctionPatterns: CorrectionPattern[];
  promptVersionStats: PromptVersionStats[];
  dateRange?: DateRangeStats;
}

export interface FieldCorrectionStats {
  field: string;
  count: number;
  percentage: number;
  examples: CorrectionExample[];
}

export interface CorrectionExample {
  original?: string;
  corrected?: string;
  checklistId?: string;
}

export interface CorrectionPattern {
  pattern: string;
  description: string;
  occurrences: number;
  affectedFields: string[];
  suggestedFix?: string;
}

export interface PromptVersionStats {
  version: string;
  extractionCount: number;
  correctionCount: number;
  correctionRate: number;
  averageConfidence: number;
  firstUsed?: string;
  lastUsed?: string;
}

export interface DateRangeStats {
  startDate: string;
  endDate: string;
  extractionsByDay: Record<string, number>;
  correctionsByDay: Record<string, number>;
}

export interface RecordCorrectionRequest {
  field: string;
  originalValue?: string;
  correctedValue?: string;
  correctedBy?: string;
  correctedByName?: string;
}

export interface DrakePromptTemplate {
  id: string;
  version: string;
  name: string;
  description?: string;
  promptText: string;
  isActive: boolean;
  createdAt: string;
  createdBy?: string;
  extractionCount: number;
  correctionCount: number;
}

// ============ DRAKE BATCH UPLOAD ============

export interface DrakeUploadBatch {
  id: string;
  taxYear: number;
  uploadedAt: string;
  uploadedBy?: string;
  uploadedByName?: string;
  totalFiles: number;
  processed: number;
  matched: number;
  pendingReview: number;
  failed: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  files: BatchFileStatus[];
  completedAt?: string;
}

export interface BatchFileStatus {
  fileName: string;
  status: 'pending' | 'completed' | 'failed';
  checklistId?: string;
  error?: string;
  processedAt?: string;
}

export interface BatchUploadResponse {
  batchId: string;
  filesQueued: number;
  estimatedTimeMinutes: number;
  message: string;
}

// ============ OUTREACH CHANNEL & STATS ============

export type OutreachChannel = 'email' | 'sms' | 'both';

export interface OutreachStats {
  totalContacted: number;
  emailsSent: number;
  smsSent: number;
  openRate: number;
  clickRate: number;
  responseRate: number;
  byDay: OutreachByDay[];
  recentActivity: OutreachActivity[];
}

export interface OutreachByDay {
  date: string;
  emails: number;
  sms: number;
  total: number;
}

export interface OutreachActivity {
  id: string;
  clientId: string;
  clientName: string;
  activityType: 'email_sent' | 'sms_sent' | 'email_opened' | 'link_clicked' | 'replied';
  channel: 'email' | 'sms';
  timestamp: string;
  details?: string;
}

export interface SendSmsOutreachRequest {
  clientIds: string[];
  taxYear: number;
  message: string;
  sentBy?: string;
  sentByName?: string;
}

export interface SmsOutreachResult {
  totalRequested: number;
  successCount: number;
  failCount: number;
  results: Array<{
    clientId: string;
    clientName?: string;
    phone?: string;
    success: boolean;
    error?: string;
  }>;
}

// ============ NOTIFICATION TYPES ============

export type NotificationType =
  | 'documents_pending'
  | 'documents_received'
  | 'ready_for_review'
  | 'changes_needed'
  | 'review_approved'
  | 'ready_for_signature'
  | 'efile_accepted'
  | 'efile_rejected'
  | 'return_results_summary'
  | 'signature_request_sent'
  | 'signature_request_resent'
  | 'signature_completed'
  | 'signature_declined'
  | 'signature_expired'
  | 'signature_voided'
  | 'document_reminder'
  | 'document_checklist'
  | 'completed_return_copy'
  | 'general_message'
  | 'review_assigned'
  | 'status_update'
  | 'appointment_reminder'
  | 'custom';

export type NotificationRecipientType = 'client' | 'preparer' | 'reviewer';

export type NotificationStatus = 'pending' | 'sent' | 'failed' | 'delivered';

export interface Notification {
  id: string;
  taxReturnId: string;
  notificationType: NotificationType;
  recipientType: NotificationRecipientType;
  recipientId?: string;
  recipientName: string;
  recipientEmail?: string;
  recipientPhone?: string;
  channel: 'email' | 'sms' | 'both';
  emailStatus?: NotificationStatus;
  smsStatus?: NotificationStatus;
  emailError?: string;
  smsError?: string;
  subject?: string;
  message?: string;
  emailSubject?: string;
  emailBody?: string;
  smsBody?: string;
  createdAt: string;
  sentAt?: string;
  triggeredBy?: string;
  triggeredByName?: string;
}

export interface NotificationTypeOption {
  id: NotificationType;
  name: string;
  description: string;
  defaultRecipient: NotificationRecipientType;
  supportsCustomMessage: boolean;
}

export interface SendManualNotificationRequest {
  taxReturnId: string;
  notificationType: NotificationType;
  recipientType: NotificationRecipientType;
  customMessage?: string;
  customEmailSubject?: string;
  customEmailBody?: string;
  additionalNotes?: string;
  attachmentBlobName?: string;
  attachmentFileName?: string;
}

export interface ResendNotificationRequest {
  taxReturnId: string;
}

// ============ CLIENT INQUIRY TYPES ============

export type InquiryStatus = 'new' | 'in_progress' | 'waiting_for_client' | 'resolved' | 'closed';
export type InquiryPriority = 'low' | 'normal' | 'high' | 'urgent';
export type InquiryCategory =
  | 'document_question'
  | 'status_inquiry'
  | 'scheduling_request'
  | 'billing_question'
  | 'tax_question'
  | 'general_inquiry'
  | 'other';

export interface InquiryMessage {
  messageId: string;
  direction: 'inbound' | 'outbound';
  fromEmail: string;
  fromName?: string;
  body: string;
  bodyPreview?: string;
  isHtml: boolean;
  receivedAt: string;
  readAt?: string;
  readBy?: string;
}

export interface ClientInquiry {
  id: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  taxReturnId?: string;
  conversationId: string;
  subject: string;
  messages: InquiryMessage[];
  status: InquiryStatus;
  priority: InquiryPriority;
  category?: InquiryCategory;
  assignedTo?: string;
  assignedToName?: string;
  assignedAt?: string;
  resolvedAt?: string;
  resolvedBy?: string;
  resolutionNotes?: string;
  createdAt: string;
  lastMessageAt: string;
}

export interface InquiryStats {
  total: number;
  new: number;
  inProgress: number;
  waitingForClient: number;
  resolved: number;
  unreadMessages: number;
  byPriority: {
    urgent: number;
    high: number;
    normal: number;
    low: number;
  };
}

export interface UpdateInquiryRequest {
  clientId: string;
  status?: InquiryStatus;
  assignedTo?: string;
  assignedToName?: string;
  priority?: InquiryPriority;
  category?: InquiryCategory;
  userId?: string;
  userName?: string;
  resolutionNotes?: string;
}

export interface ReplyToInquiryRequest {
  clientId: string;
  body: string;
  senderName?: string;
  senderId?: string;
}

export const inquiryStatusDisplayNames: Record<InquiryStatus, string> = {
  new: 'New',
  in_progress: 'In Progress',
  waiting_for_client: 'Waiting for Client',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const inquiryStatusColors: Record<InquiryStatus, string> = {
  new: '#0078d4',       // blue
  in_progress: '#8B5CF6', // purple
  waiting_for_client: '#F59E0B', // amber
  resolved: '#10B981',  // green
  closed: '#6B7280',    // gray
};

export const inquiryPriorityDisplayNames: Record<InquiryPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

export const inquiryPriorityColors: Record<InquiryPriority, string> = {
  low: '#6B7280',      // gray
  normal: '#3B82F6',   // blue
  high: '#F59E0B',     // amber
  urgent: '#EF4444',   // red
};

export const inquiryCategoryOptions: { value: InquiryCategory; label: string }[] = [
  { value: 'document_question', label: 'Document Question' },
  { value: 'status_inquiry', label: 'Status Inquiry' },
  { value: 'scheduling_request', label: 'Scheduling Request' },
  { value: 'billing_question', label: 'Billing Question' },
  { value: 'tax_question', label: 'Tax Question' },
  { value: 'general_inquiry', label: 'General Inquiry' },
  { value: 'other', label: 'Other' },
];

// ============ IRS LETTER / NOTICE TYPES ============
//
// The feature exists to pass one test: a client calls with a letter in their hand and the staff
// member who picks up answers — what letter, when it arrived, when it is due, who owns it, where
// we are, and did we already amend — without hanging up and without opening a second system.

export type IrsNoticeStatus =
  | 'received'
  | 'under_review'
  | 'awaiting_client_info'
  | 'amendment_in_progress'
  | 'awaiting_irs_response'
  | 'resolved'
  | 'closed_no_action';

export type NoticeAuthority = 'irs' | 'state' | 'local';

export interface NoticeStatusChange {
  fromStatus?: string;
  toStatus: string;
  changedAt: string;
  changedBy?: string;
  changedByName?: string;
  notes?: string;
}

export interface NoticeAmendmentLink {
  taxReturnId: string;
  taxYear: number;
  linkedAt: string;
  linkedBy?: string;
  linkedByName?: string;
}

/**
 * A linked amendment as the reader sees it, with status resolved LIVE from the linked return at
 * request time — never copied onto the notice, so it cannot go stale.
 *
 * `status` is the RAW TaxReturnStatus, rendered through the existing `statusDisplayNames` map in
 * this file. The server deliberately does not ship a label for it: that map already lives here,
 * and a second copy server-side would be two implementations of one invariant. (The notice's own
 * status IS shipped with a label and colour, because that vocabulary is new and server-owned.)
 *
 * `isComplete` is nullable on purpose. A return that could not be READ is not the same fact as a
 * return NOT YET ACCEPTED — rendering unknown as false would let the resolve guard look satisfied
 * on evidence nobody has.
 */
export interface NoticeAmendmentView {
  taxReturnId: string;
  taxYear: number;
  status?: TaxReturnStatus | null;
  isComplete?: boolean | null;
}

export interface IrsNotice {
  id: string;
  clientId: string;
  clientName: string;
  contactId?: string;
  contactName?: string;
  taxReturnId: string;
  appointmentId?: string;
  taxYear: number;
  noticeNumber: string;
  noticeReceivedDate: string;
  noticeDate?: string;
  responseDueDate?: string;
  summary?: string;
  authority: NoticeAuthority;
  amountAtStake?: number;
  assignedTo?: string;
  assignedToName?: string;
  assignedAt?: string;
  status: IrsNoticeStatus;
  statusHistory: NoticeStatusChange[];
  amendmentLinks: NoticeAmendmentLink[];
  documentIds: string[];
  resolvedAt?: string;
  resolvedBy?: string;
  resolvedByName?: string;
  resolutionNotes?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  createdByName?: string;
}

/** A notice plus the values the server resolves at read time. Never stored. */
export interface IrsNoticeDetail {
  notice: IrsNotice;
  amendments: NoticeAmendmentView[];
  daysUntilDue?: number | null;
  isOpen: boolean;
  statusDisplayName: string;
  statusColor: string;
}

export interface IrsNoticeStats {
  total: number;
  open: number;
  unassigned: number;
  overdue: number;
  dueSoon: number;
  byStatus: Record<string, number>;
}

export interface CreateIrsNoticeRequest {
  noticeNumber: string;
  noticeReceivedDate?: string;
  noticeDate?: string;
  responseDueDate?: string;
  summary?: string;
  amountAtStake?: number;
  authority?: NoticeAuthority;
  documentIds?: string[];
  assignedTo?: string;
  assignedToName?: string;
  /** The parent return's partition key — lets the server do a point-read instead of a fan-out. */
  appointmentId?: string;
  // ⚠ No clientId / taxYear / contactId. The server derives those FROM THE RETURN; sending them
  // would be ignored. Writing the wrong guid does not error, it silently orphans the notice.
}

export interface UpdateIrsNoticeRequest {
  /** Required — the partition key. */
  clientId: string;
  status?: IrsNoticeStatus;
  statusNotes?: string;
  assignedTo?: string;
  assignedToName?: string;
  noticeReceivedDate?: string;
  noticeDate?: string;
  responseDueDate?: string;
  noticeNumber?: string;
  summary?: string;
  resolutionNotes?: string;
  amountAtStake?: number;
  documentIds?: string[];
}

export interface LinkAmendmentRequest {
  clientId: string;
  taxReturnId: string;
  appointmentId?: string;
}

export const irsNoticeStatusDisplayNames: Record<IrsNoticeStatus, string> = {
  received: 'Received',
  under_review: 'Under Review',
  awaiting_client_info: 'Awaiting Client Info',
  amendment_in_progress: 'Amendment in Progress',
  awaiting_irs_response: 'Awaiting IRS Response',
  resolved: 'Resolved',
  closed_no_action: 'Closed — No Action',
};

export const irsNoticeStatusColors: Record<IrsNoticeStatus, string> = {
  received: '#6B7280',              // grey — logged, unowned
  under_review: '#8B5CF6',          // violet — someone is reading it
  awaiting_client_info: '#F59E0B',  // amber — blocked on the client
  amendment_in_progress: '#3B82F6', // blue — the amendment is the work
  awaiting_irs_response: '#0EA5E9', // sky — ball is with the IRS
  resolved: '#10B981',              // green — closed, outcome known
  closed_no_action: '#64748B',      // slate — closed, nothing was owed
};

/**
 * The two terminal statuses. Mirrors the server's single definition of `isOpen`.
 * The server is authoritative — it computes `isOpen` on every response — but the queue needs to
 * filter client-side without a round trip, so the list is duplicated here deliberately and the
 * duplication is named rather than hidden.
 */
export const irsNoticeClosedStatuses: IrsNoticeStatus[] = ['resolved', 'closed_no_action'];

export const isIrsNoticeOpen = (status: IrsNoticeStatus): boolean =>
  !irsNoticeClosedStatuses.includes(status);

export const irsNoticeStatusOptions: { value: IrsNoticeStatus; label: string }[] =
  (Object.keys(irsNoticeStatusDisplayNames) as IrsNoticeStatus[])
    .map((value) => ({ value, label: irsNoticeStatusDisplayNames[value] }));

// ============ CLIENT OPT-OUT TYPES ============

export type OptOutReason =
  | 'not_filing_this_year'
  | 'using_different_preparer'
  | 'doing_own_taxes'
  | 'deceased'
  | 'moved_away'
  | 'unsubscribe_request'
  | 'other';

export type OptOutChannel = 'email' | 'sms' | 'both' | 'none';

export interface ClientOptOut {
  id: string;
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  clientType: 'contact' | 'account';
  taxYear: number;
  reason: OptOutReason;
  reasonDetails?: string;
  optOutChannel: OptOutChannel;
  optOutDate: string;
  recordedBy?: string;
  recordedByName?: string;
  source: 'manual' | 'email_reply' | 'sms_reply' | 'unsubscribe_link';
  isActive: boolean;
  reactivatedAt?: string;
  reactivatedBy?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OptOutStats {
  total: number;
  byReason: Record<OptOutReason, number>;
  byChannel: Record<OptOutChannel, number>;
  thisYear: number;
  reactivated: number;
}

export interface CreateOptOutRequest {
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  clientType: 'contact' | 'account';
  taxYear: number;
  reason: OptOutReason;
  reasonDetails?: string;
  optOutChannel: OptOutChannel;
  source: 'manual' | 'email_reply' | 'sms_reply' | 'unsubscribe_link';
  notes?: string;
}

export interface UpdateOptOutRequest {
  reason?: OptOutReason;
  reasonDetails?: string;
  optOutChannel?: OptOutChannel;
  notes?: string;
  isActive?: boolean;
}

export const optOutReasonDisplayNames: Record<OptOutReason, string> = {
  not_filing_this_year: 'Not Filing This Year',
  using_different_preparer: 'Using Different Preparer',
  doing_own_taxes: 'Doing Own Taxes',
  deceased: 'Deceased',
  moved_away: 'Moved Away',
  unsubscribe_request: 'Unsubscribe Request',
  other: 'Other',
};

export const optOutReasonColors: Record<OptOutReason, string> = {
  not_filing_this_year: '#F59E0B',     // amber
  using_different_preparer: '#EF4444', // red
  doing_own_taxes: '#3B82F6',          // blue
  deceased: '#6B7280',                 // gray
  moved_away: '#8B5CF6',               // purple
  unsubscribe_request: '#EC4899',      // pink
  other: '#6B7280',                    // gray
};

export const optOutChannelDisplayNames: Record<OptOutChannel, string> = {
  email: 'Email Only',
  sms: 'SMS Only',
  both: 'All Communications',
  none: 'No Opt-Out',
};

export const optOutReasonOptions: { value: OptOutReason; label: string }[] = [
  { value: 'not_filing_this_year', label: 'Not Filing This Year' },
  { value: 'using_different_preparer', label: 'Using Different Preparer' },
  { value: 'doing_own_taxes', label: 'Doing Own Taxes' },
  { value: 'deceased', label: 'Deceased' },
  { value: 'moved_away', label: 'Moved Away' },
  { value: 'unsubscribe_request', label: 'Unsubscribe Request' },
  { value: 'other', label: 'Other' },
];

// ============ IN-HOUSE CLIENT STATUS TYPES ============

export type InHouseService = 'bookkeeping' | 'payroll' | 'salestax';
export type ContactRelationship = 'primary_contact' | 'contact_person_2' | 'contact_person_3';

export interface TaxPrepStatus {
  hasAppointment: boolean;
  hasChecklist: boolean;
  hasOutreach: boolean;
  isSetUp: boolean;  // true if hasAppointment OR hasChecklist
  appointmentId?: string;
  appointmentDate?: string;
  taxReturnId?: string;
  taxReturnStatus?: string;
  checklistId?: string;
  checklistMatchStatus?: string;
  lastOutreachDate?: string;
}

export interface InHouseContact {
  contactId: string;
  contactName: string;
  email?: string;
  phone?: string;
  relationship: ContactRelationship;
  taxPrepStatus: TaxPrepStatus;
}

export interface InHouseClient {
  accountId: string;
  accountName: string;
  accountEmail?: string;
  accountPhone?: string;
  inHouseServices: InHouseService[];
  serviceOwners?: Record<string, string | null>; // e.g., {"bookkeeping": "John Smith", "payroll": "Jane Doe"}
  relatedContacts: InHouseContact[];
  accountTaxPrepStatus: TaxPrepStatus;
}

export interface InHouseClientsResponse {
  taxYear: number;
  totalAccounts: number;
  totalContacts: number;
  accountsSetUp: number;
  accountsMissing: number;
  contactsSetUp: number;
  contactsMissing: number;
  clients: InHouseClient[];
}

export const inHouseServiceDisplayNames: Record<InHouseService, string> = {
  bookkeeping: 'Bookkeeping',
  payroll: 'Payroll',
  salestax: 'Sales Tax',
};

export const contactRelationshipDisplayNames: Record<ContactRelationship, string> = {
  primary_contact: 'Primary Contact',
  contact_person_2: 'Contact Person 2',
  contact_person_3: 'Contact Person 3',
};

// ============ ZOHO SIGN E-SIGNATURE TYPES ============

export type BusinessEntityType = 'c-corp' | 's-corp' | 'partnership';

export const businessEntityTypeToFormType: Record<BusinessEntityType, Form8879Type> = {
  'c-corp': '8879-C',
  's-corp': '8879-S',
  'partnership': '8879-PE',
};

export type Form8879Type = '8879' | '8879-C' | '8879-S' | '8879-PE';

export const Form8879TypeDisplayNames: Record<Form8879Type, string> = {
  '8879': '8879 - Individual',
  '8879-C': '8879-C - C Corporation',
  '8879-S': '8879-S - S Corporation',
  '8879-PE': '8879-PE - Partnership',
};

export const Form8879TypeDescriptions: Record<Form8879Type, string> = {
  '8879': 'IRS e-file Signature Authorization for Individual Tax Returns',
  '8879-C': 'IRS e-file Signature Authorization for C Corporations',
  '8879-S': 'IRS e-file Signature Authorization for S Corporations',
  '8879-PE': 'IRS e-file Signature Authorization for Partnerships',
};

export interface SendForSignatureRequest {
  documentBlobName: string;
  formType?: Form8879Type;
  clientEmail?: string;
  spouseEmail?: string;
  authorizedSignerName?: string;
  authorizedSignerEmail?: string;
}

export interface SignatureValidationResponse {
  isValid: boolean;
  missingFields: string[];
  fieldDescriptions: Record<string, string>;
  formType: string;
  formTypeDisplayName: string;
}

export interface ZohoSignRequest {
  requestId: string;
  requestStatus: string;
  documentIds: string[];
  signers: ZohoSignSigner[];
  createdAt: string;
  expiresAt?: string;
  signUrl?: string;
}

export interface ZohoSignSigner {
  name: string;
  email: string;
  role: string;
  status: string;
  signedAt?: string;
}

export interface SignatureStatusResponse {
  taxReturnId: string;
  signatureStatus: 'pending' | 'sent' | 'signed';
  signatureRequestId?: string;
  signatureSentDate?: string;
  signatureCompletedDate?: string;
  signatureFormType?: Form8879Type;
  zohoSignRequest?: ZohoSignRequest;
}

/**
 * An external Zoho Sign envelope that staff linked to a tax return after initiating
 * it outside the app (state 8879, Trump-account-election, etc.). Primary Signature*
 * fields on TaxReturn stay dedicated to the federal 8879 from SendForSignature.
 */
export interface LinkedSignature {
  requestId: string;
  displayName: string;
  formType?: string;
  status: 'pending' | 'sent' | 'signed' | 'declined' | 'expired' | 'voided';
  sentDate?: string;
  completedDate?: string;
  signerEmails?: string[];
  linkedAt: string;
  linkedBy?: string;
  signedBlobName?: string;
  notes?: string;
}

export interface LinkSignatureRequest {
  requestId: string;
  displayName?: string;
  formType?: string;
  notes?: string;
}

/**
 * Server response from POST /tax-returns/{id}/signature/link/{requestId}/refresh.
 * `linked` carries the synced LinkedSignature row; the flags describe what changed
 * during this refresh so the UI can show meaningful feedback ("PDF saved" / "Status
 * advanced to ReadyForEFile").
 */
export interface RefreshLinkedSignatureResult {
  linked: LinkedSignature;
  zohoStatus: string;
  pdfSaved: boolean;
  federalStatusAdvanced: boolean;
  previousReturnStatus?: string;
  newReturnStatus?: string;
}

// ============ CORRECTION REQUEST TYPES ============

export type CorrectionDocumentType = '1095-A' | 'W-2' | '1099' | 'other';

export interface RequestCorrectionRequest {
  documentType: CorrectionDocumentType | string;
  notes?: string;
  language?: 'en' | 'es';
}

export interface RequestCorrectionResponse {
  uploadUrl: string;
  emailSent: boolean;
  smsSent: boolean;
  emailSkipReason?: string;
  smsSkipReason?: string;
}

// ============ SHARED INVOICE TYPES ============

export type SharedInvoiceStatus = 'draft' | 'sent' | 'paid' | 'partial' | 'void';

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  accountCode?: string;
}

export interface TaxReturnAllocation {
  taxReturnId: string;
  clientName?: string;
  returnType?: string;
  allocatedAmount: number;
  paidAmount: number;
  isPaid: boolean;
  allocationPercentage: number;
}

export interface ClientInvoice {
  id: string;
  invoiceGroupId: string;
  invoiceNumber?: string;
  xeroInvoiceId?: string;
  status: SharedInvoiceStatus;
  totalAmount: number;
  amountPaid: number;
  amountDue: number;
  lineItems: InvoiceLineItem[];
  taxReturnAllocations: TaxReturnAllocation[];
  paymentUrl?: string;
  xeroUrl?: string;
  primaryClientId?: string;
  primaryClientName?: string;
  taxYear: number;
  invoiceDate?: string;
  dueDate?: string;
  paidDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateSharedInvoiceRequest {
  taxReturnIds: string[];
  lineItems?: InvoiceLineItem[];
  allocations?: { taxReturnId: string; allocatedAmount: number }[];
  createInXero?: boolean;
  primaryTaxReturnId?: string;  // Which return's client is the invoice recipient
  xeroContactId?: string;
  forceCreateContact?: boolean;
  scheduleCBusinessId?: string;  // Optional: bill from a specific Schedule C business on the recipient return
}

export interface XeroContact {
  contactId: string;
  name: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface AllocatePaymentRequest {
  amount: number;
  paymentDate?: string;
  reference?: string;
}

export const sharedInvoiceStatusDisplayNames: Record<SharedInvoiceStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  partial: 'Partially Paid',
  void: 'Void',
};

export const sharedInvoiceStatusColors: Record<SharedInvoiceStatus, string> = {
  draft: '#6B7280',    // gray
  sent: '#3B82F6',     // blue
  paid: '#10B981',     // green
  partial: '#F59E0B',  // amber
  void: '#EF4444',     // red
};

// ============ PREPARER BILLING TYPES ============

export type PreparerBillStatus = 'draft' | 'generated' | 'paid';

export interface BillLineItem {
  taxReturnId: string;
  clientName: string;
  returnType: string;
  amount: number;
  source: string;
  amountSource?: string;
  description?: string;
  completedAt?: string;
}

export interface PreparerBill {
  id: string;
  preparerId: string;
  preparerName: string;
  preparerEmail?: string;
  taxYear: number;
  periodStart?: string;
  periodEnd?: string;
  taxReturnIds: string[];
  lineItems: BillLineItem[];
  totalAmount: number;
  commissionRate: number; // Rate used for this bill (e.g., 0.30 for 30%)
  status: PreparerBillStatus;
  generatedDate?: string;
  paidDate?: string;
  paymentMethod?: string;
  paymentReference?: string;
  xeroBillId?: string;
  xeroBillNumber?: string;
  xeroUrl?: string;
  notes?: string;
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  updatedAt: string;
}

export interface GeneratePreparerBillRequest {
  preparerId: string;
  preparerName: string;
  preparerEmail?: string;
  taxYear: number;
  taxReturnIds: string[];
  createdBy?: string;
  createdByName?: string;
  notes?: string;
  commissionRate?: number; // Optional - defaults to 0.30 (30%) if not specified
}

// Summary of unbilled returns by preparer (for admin bulk billing view)
export interface PreparerUnbilledSummary {
  preparerId: string;
  preparerName: string;
  preparerEmail?: string;
  unbilledCount: number;
  totalInvoicedAmount: number;
}

export interface XeroBillResult {
  success: boolean;
  billId?: string;
  billNumber?: string;
  totalAmount: number;
  xeroUrl?: string;
  error?: string;
}

export interface RecordPaymentRequest {
  preparerId: string;
  paymentMethod: string;
  paymentReference?: string;
  amount?: number;
  paidDate?: string;
}

export const preparerBillStatusDisplayNames: Record<PreparerBillStatus, string> = {
  draft: 'Draft',
  generated: 'Generated',
  paid: 'Paid',
};

export const preparerBillStatusColors: Record<PreparerBillStatus, string> = {
  draft: '#6B7280',     // gray
  generated: '#3B82F6', // blue
  paid: '#10B981',      // green
};

// ============ TAX CLIENT TYPES ============

export interface TaxClientListItem {
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  clientType: 'individual' | 'business';
  taxYears: number[];
  returnCount: number;
  latestStatus?: string;
  latestReturnId?: string;
  xeroContactId?: string;
  xeroContactIdUpdatedAt?: string;
  xeroContactIdUpdatedBy?: string;
  contactId?: string;
  scheduleCBusinessName?: string;
}

// ============ FILING HISTORY IMPORT + MATCH REVIEW TYPES ============

export type FilingClassifiedStatus = 'Filed' | 'ExtensionOnly' | 'NotFiled' | 'Review';

export type MatchConfidence = 'auto' | 'confirmed' | 'pending';

export interface FilingImportSummary {
  importBatchId: string;
  rowsTotal: number;
  rowsImported: number;
  autoMatched: number;
  confirmed: number;
  pending: number;
  classifiedAsReview: number;
  errors: Array<{ rowNumber: number; field: string; message: string }>;
}

export interface MatchReviewCandidate {
  id: string;
  fullName: string;
  email?: string;
  phone?: string;
  last4Matches: boolean;
}

export interface MatchReviewRow {
  id: string;
  taxYear: number;
  last4: string;
  clientNameRaw: string;
  returnType: string;
  rawStatus: string;
  lastChangeDate?: string | null;
  candidates: MatchReviewCandidate[];
}

export interface MatchReviewPage {
  items: MatchReviewRow[];
  total: number;
  nextSkip: number | null;
}

export type ContactSearchKind = 'contact' | 'account' | 'both';

export interface BootstrapProposal {
  id: string;
  currentName: string;
  proposedContactId: string | null;
  proposedContactName: string | null;
  source: 'primary' | 'spouse' | 'scheduleC' | 'account' | 'ambiguous';
  committed: boolean;
}

export interface BootstrapSkip {
  id: string;
  reason: string;
}

export interface BootstrapPassResponse {
  processed: number;
  autoMatched: number;
  ambiguous: number;
  noMatch: number;
  proposals: BootstrapProposal[];
  skipped: BootstrapSkip[];
  nextCursor: number | null;
  total: number;
}

export interface ContactSearchResult {
  id: string;
  fullName: string;
  email?: string;
  phone?: string;
  last4Matches: boolean;
  kind: 'contact' | 'account';
}

// ============ PRIORITY OUTREACH TYPES ============

export type PriorityBucket = 'two_year_no_current' | 'one_year_no_current' | 'extension_current_pending';

export type PriorityRowStatus = 'open' | 'contacted' | 'scheduled' | 'opted_out' | 'dismissed';

export interface PriorityRow {
  contactId: string;
  contactName: string;
  email?: string;
  phone?: string;
  bucket: PriorityBucket;
  returnType: string;
  priorYearTotalBill?: number | null;
  lastFiledAt?: string | null;
  extensionFiledAt?: string | null;
  status?: PriorityRowStatus;
  notes?: string;
}

export interface PriorityListResponse {
  rows: PriorityRow[];
  bucketCounts: Record<PriorityBucket, number>;
  /** Total rows in the selected bucket (pre-pagination). Drives the range label and Prev/Next. */
  total: number;
  /** The effective skip value applied for this page. */
  skip: number;
  /** The effective take (page size) applied for this page. */
  take: number;
  /** The skip value for the next page, or null when this is the last page. */
  nextSkip: number | null;
}

/**
 * In-app notification addressed to a STAFF member (not to a client).
 *
 * Mirrors the backend `StaffNotification`. `readAt === null` means unread. Until 2026-08-27 the
 * container behind this type had a writer and no reader, so every row ever written was unread by
 * construction; this type exists because that was fixed.
 */
export interface StaffNotification {
  id: string;
  recipientStaffId: string;
  /** Discriminator, persisted as int. 1 = PortalUploadReceived. */
  kind: number;
  taxReturnId?: string | null;
  taxDocumentId?: string | null;
  clientId?: string | null;
  title: string;
  body?: string | null;
  /** When the recipient read it. Null = unread. */
  readAt?: string | null;
  createdAt: string;
}

/** A page of staff notifications plus the unread badge count. */
export interface StaffNotificationFeed {
  items: StaffNotification[];
  unreadCount: number;
}
